import Button, { ButtonTypes } from '@/components/Buttons/Button';
import { Divider, Textarea } from '@mantine/core';
import {
  useGetArchitectureDetail,
  useGetArchitectureDetailComment,
} from '@/features/Architecture/hook/api';

import { ACTIVITY_TYPE } from '@/consts/Activity';
import { API_ENDPOINT } from '@/config/endpoint';
import CommentCard from '@/components/Comment/CommentCard';
import ConfirmationPopup from '@/components/ConfirmationPopup';
import DMText from '@/components/DMText';
import Image from 'next/image';
import Toast from '@/components/Toast';
import { createArchitectureDetailPageStyles } from '@/features/Architecture/architecture.style';
import { images } from '@/utils/images';
import { isSignedIn } from '@/config/globalStateControl';
import { messages } from '@/consts/Messages';
import themeVariables from '@/styles/themeVariables';
import { useAtomValue } from 'jotai';
import { useParams } from 'next/navigation';
import { useState } from 'react';

const ArchDetailComment = () => {
  const { id } = useParams();
  const { classes } = createArchitectureDetailPageStyles();

  const isSigned = useAtomValue(isSignedIn);

  const { data } = useGetArchitectureDetail(id);
  const { comment_count } = data || {};

  const { data: comments, refetch: refetchComment } =
    useGetArchitectureDetailComment(id);

  const [newComment, setNewComment] = useState('');

  const handleLikeComment = async (commentId, enable_like) => {
    await API_ENDPOINT.comment
      .updateCommentLikeCount(commentId, enable_like)
      .then(() => {
        refetchComment();
      });
  };

  const handleEditComment = async (parentId, commentId, text) => {
    if (text.length == 0) {
      Toast.info(messages.ADD_COMMENT_VACANT);
    }

    await API_ENDPOINT.comment
      .editComment(commentId, text)
      .then(() => refetchComment());
  };

  const handleAddComment = async (parentId, text) => {
    if (!isSigned) {
      return Toast.info(messages.ADD_COMMENT_NOT_SIGNEDIN_USER);
    }

    if (text.length == 0) {
      return Toast.info(messages.ADD_COMMENT_VACANT);
    }

    await API_ENDPOINT.comment
      .addComment(ACTIVITY_TYPE.architecture, id, parentId, text)
      .then(() => refetchComment());

    setNewComment('');
  };

  const handleDeleteComment = (commentId) => {
    ConfirmationPopup.confirm('댓글을 삭제하시겠습니까?', async () => {
      await API_ENDPOINT.comment
        .deleteComment(commentId)
        .then(() => refetchComment());
    });
  };

  const handleSubmitNewCommentButton = async () => {
    await handleAddComment(null, newComment);
    setNewComment('');
  };

  return (
    <>
      <DMText
        className={classes.commentTitle}
        dColor={themeVariables.darkMode.strong}
      >
        댓글 ({comment_count})
      </DMText>
      <Divider my={20} />
      {comments?.length == 0 && (
        <div className={classes.commentNoResultContainer}>
          <Image
            width={60}
            height={60}
            src={images.noCommentGif}
            alt="comment"
            quality={60}
            priority
            style={{ borderRadius: 10 }}
          />
          <DMText dColor={themeVariables.darkMode.normal}>
            자유롭게 의견을 남겨보세요
          </DMText>
        </div>
      )}
      {comments?.map((comment, idx) => (
        <div className={'fadeInUpAnimation'} key={idx}>
          <CommentCard
            comment={comment}
            nestedComment={comment.children}
            handleAddComment={handleAddComment}
            handleLikeComment={handleLikeComment}
            handleEditComment={handleEditComment}
            handleDeleteComment={handleDeleteComment}
          />
        </div>
      ))}
      <div className={classes.addCommentContainer}>
        <DMText
          className={classes.addCommentTitle}
          dColor={themeVariables.darkMode.strong}
        >
          댓글 쓰기
        </DMText>
        <Textarea
          minRows={3}
          variant={'filled'}
          value={newComment}
          placeholder={'작성하실 댓글을 남겨주세요.'}
          onChange={(e) => setNewComment(e.target.value)}
        />
        <div className={classes.addCommentButtonContainer}>
          <Button
            type={ButtonTypes.gray}
            onClick={handleSubmitNewCommentButton}
          >
            작성하기
          </Button>
        </div>
      </div>
    </>
  );
};

export default ArchDetailComment;
